import { useEffect, useRef, useState } from 'react'
import type { UiNode } from '@ory/client'

import { hasProviderNode } from '@utils/kratos-nodes.utils'
import { fetchHrdConfig, fetchHrdLookup } from '@utils/hrd.utils'
import type { SsoBrand } from '@utils/oidc-providers.utils'
import {
  clearRememberedProvider,
  readRememberedProvider,
  writeRememberedProvider,
} from '@utils/hrd-remember.utils'

export type EmailStepState =
  | { kind: 'closed' }
  | {
      kind: 'open'
      pending: boolean
      error: 'domain_not_connected' | 'lookup_failed' | 'rate_limited' | null
    }

// Reduced from LoginPage's flow state to exactly what this hook needs:
// 'loading' = placeholder oidcNodes (flow not loaded yet), 'flow' = real
// oidcNodes, 'terminal' = the SSO block never renders.
export type SsoRoutePhase = 'loading' | 'flow' | 'terminal'

export interface UseSsoRouteOptions {
  /**
   * The dedicated /login/sso page has no primary button to click — submit a
   * valid remembered route the instant the flow loads instead of waiting for
   * one. Unused (default false) on /login, where a click always drives it.
   */
  autoSubmitRemembered?: boolean
  /**
   * Fired when a primary-brand click can't resolve a provider (no configured
   * default, or /hrd/config unreachable). The email-step UI only exists on
   * /login/sso, so any page with a primary button MUST supply this to route
   * the user there — otherwise the click would be a silent no-op.
   */
  onNoPrimaryRoute?: () => void
}

export interface UseSsoRouteResult {
  /** Provider id to plug into the login form's hidden `provider` field. Non-null exactly once a submit is in flight. */
  pendingProvider: string | null
  /** True once /hrd/config has settled (success or final failure) — gates the primary button so an early click can't race an unresolved default. */
  configReady: boolean
  /** Drives the "Use a different account" escape link. */
  hasRememberedRoute: boolean
  emailStep: EmailStepState
  submitPrimary: (brand: SsoBrand) => void
  openEmailStep: () => void
  useDifferentAccount: () => void
  submitEmailLookup: (email: string) => void
}

// LoginPage wires the resulting `pendingProvider` into the hidden `provider`
// field and calls `form.submit()` — see LoginPage.container.tsx for why not
// `.requestSubmit()`.
export const useSsoRoute = (
  oidcNodes: ReadonlyArray<UiNode>,
  phase: SsoRoutePhase,
  options: UseSsoRouteOptions = {},
): UseSsoRouteResult => {
  const { autoSubmitRemembered = false, onNoPrimaryRoute } = options
  const [rememberedProvider, setRememberedProvider] = useState<string | null>(() =>
    readRememberedProvider(),
  )
  const [brandProviders, setBrandProviders] = useState<Record<string, string | null>>({})
  const [configReady, setConfigReady] = useState(false)
  const [pendingProvider, setPendingProvider] = useState<string | null>(null)
  const [emailStep, setEmailStep] = useState<EmailStepState>({ kind: 'closed' })

  // 'terminal' never renders the SSO block, so skip the fetch. 'loading'
  // still fetches, in parallel with the flow request (avoids a sequential
  // round trip). Depend on this boolean, not `phase`, so loading→flow
  // doesn't re-trigger a second fetch.
  const needsSso = phase !== 'terminal'

  useEffect(() => {
    if (!needsSso) {
      return () => {}
    }
    const controller = new AbortController()
    const load = async (): Promise<void> => {
      let result = await fetchHrdConfig(controller.signal)
      if (result.kind === 'error' && !controller.signal.aborted) {
        // One retry — a cold-start backend or a single dropped request
        // shouldn't force every login onto the email step.
        result = await fetchHrdConfig(controller.signal)
      }
      if (controller.signal.aborted) {
        return
      }
      if (result.kind === 'ok') {
        setBrandProviders(result.brandProviders)
      }
      setConfigReady(true)
    }
    load()
    return () => controller.abort()
  }, [needsSso])

  const hasRememberedRoute =
    rememberedProvider !== null && hasProviderNode(oidcNodes, rememberedProvider)

  // Drop a remembered provider Kratos no longer offers (config change,
  // offboarded customer) so the primary button falls back to the default.
  // Gated on `phase === 'flow'`, not oidcNodes.length: during 'loading' the
  // empty placeholder array is indistinguishable from a genuinely SSO-less
  // flow, and would wipe a still-valid remembered provider prematurely.
  const flowLoaded = phase === 'flow'
  useEffect(() => {
    if (flowLoaded && rememberedProvider !== null && !hasRememberedRoute) {
      clearRememberedProvider()
      setRememberedProvider(null)
    }
  }, [flowLoaded, rememberedProvider, hasRememberedRoute])

  // SSO-page-only: fires once a valid route is confirmed — that page isn't
  // tied to a brand click the way submitPrimary is.
  useEffect(() => {
    if (autoSubmitRemembered && flowLoaded && rememberedProvider !== null && hasRememberedRoute) {
      setPendingProvider(rememberedProvider)
    }
  }, [autoSubmitRemembered, flowLoaded, rememberedProvider, hasRememberedRoute])

  const submitPrimary = (brand: SsoBrand): void => {
    // An explicit brand click is explicit intent — it always resolves through
    // that brand's HRD_BRAND_PROVIDERS default. A remembered email-lookup
    // route must never hijack it (looking up one customer's SAML domain used
    // to reroute "Sign in with Microsoft" to that customer's IdP); remembered
    // routes only drive the /login/sso auto-submit above.
    const brandDefault = brandProviders[brand] ?? null
    if (brandDefault !== null && hasProviderNode(oidcNodes, brandDefault)) {
      setPendingProvider(brandDefault)
      return
    }
    // No resolvable provider for this brand — hand off to the caller (on
    // /login that's a redirect to /login/sso, where the email step lives).
    onNoPrimaryRoute?.()
  }

  const openEmailStep = (): void => {
    setEmailStep({ kind: 'open', pending: false, error: null })
  }

  const useDifferentAccount = (): void => {
    clearRememberedProvider()
    setRememberedProvider(null)
    setEmailStep({ kind: 'open', pending: false, error: null })
  }

  const lookupControllerRef = useRef<AbortController | null>(null)
  useEffect(() => () => lookupControllerRef.current?.abort(), [])

  const submitEmailLookup = (email: string): void => {
    lookupControllerRef.current?.abort()
    const controller = new AbortController()
    lookupControllerRef.current = controller

    setEmailStep({ kind: 'open', pending: true, error: null })
    fetchHrdLookup(email, controller.signal).then((result) => {
      if (controller.signal.aborted) {
        return
      }
      if (result.kind === 'found') {
        // HRD table and Kratos flow can drift (stale entry, rollout in
        // progress) — never auto-submit a provider the flow doesn't offer.
        if (!hasProviderNode(oidcNodes, result.provider)) {
          setEmailStep({ kind: 'open', pending: false, error: 'domain_not_connected' })
          return
        }
        // Only the lookup path remembers a route — it feeds the /login/sso
        // auto-submit for returning users. Brand clicks never read it.
        writeRememberedProvider(result.provider)
        setRememberedProvider(result.provider)
        setPendingProvider(result.provider)
        return
      }
      setEmailStep({
        kind: 'open',
        pending: false,
        error:
          result.kind === 'not_found'
            ? 'domain_not_connected'
            : result.kind === 'rate_limited'
              ? 'rate_limited'
              : 'lookup_failed',
      })
    })
  }

  return {
    pendingProvider,
    configReady,
    hasRememberedRoute,
    emailStep,
    submitPrimary,
    openEmailStep,
    useDifferentAccount,
    submitEmailLookup,
  }
}
